import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Select, message, Spin } from "antd";
import { getGroupMembersByGroupNoApi, assignAssessorApi } from "../../service/profile";
/// <reference path="../../types/api.d.ts" />

/** 与原先 `AssessorModal.scss` 中 `.auto-model-content.assessor-modal` 一致 */
export const ASSESSOR_MODAL_CONTENT_CLASS =
  "assessor-modal !max-h-none !overflow-hidden !rounded-lg !px-6 !py-0 max-md:!max-w-screen max-md:!rounded-none " +
  "[&_.auto-model__header]:mb-0 [&_.auto-model__header]:border-b [&_.auto-model__header]:border-[#f0f0f0] [&_.auto-model__header]:px-0 [&_.auto-model__header]:py-4 " +
  "[&_.auto-model__title]:text-base [&_.auto-model__title]:font-semibold [&_.auto-model__title]:text-[#262626] [&_.auto-model__title_*]:text-[#262626] " +
  "[&_.auto-model__body]:gap-0 [&_.auto-model__body]:py-6 [&_.auto-model__body]:!px-0 " +
  "[&_.auto-model__footer]:mt-0 [&_.auto-model__footer]:border-t [&_.auto-model__footer]:border-[#f0f0f0] [&_.auto-model__footer]:px-0 [&_.auto-model__footer]:py-3";

const bodyClass =
  "[&_.modal-text]:mb-4 [&_.modal-text]:text-sm [&_.modal-text]:leading-normal [&_.modal-text]:text-[#595959]";

const loadingRowClass = "flex items-center justify-center py-5";

const loadingTextClass = "ml-2 text-sm text-[#8c8c8c]";

const selectClass =
  "assessor-select mb-3 w-full " +
  "[&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:cursor-pointer [&_.ant-select-selector]:rounded [&_.ant-select-selector]:border [&_.ant-select-selector]:border-[#d9d9d9] [&_.ant-select-selector]:text-sm " +
  "[&_.ant-select-selector:hover]:border-[#40a9ff] [&_.ant-select-selector:focus-within]:border-[#40a9ff] [&_.ant-select-selector:focus-within]:shadow-[0_0_0_2px_rgba(24,144,255,0.2)] " +
  "[&_.ant-select-selection-item]:!leading-[38px] [&_.ant-select-selection-item]:text-[#262626] " +
  "[&_.ant-select-selection-placeholder]:leading-[38px] [&_.ant-select-selection-placeholder]:text-[#bfbfbf] " +
  "[&_.ant-select-selection-search-input]:leading-[38px] " +
  "[&_.ant-select-arrow]:text-[#8c8c8c] [&_.ant-select-arrow]:transition-transform [&_.ant-select-arrow]:duration-300 [&_.ant-select-arrow-icon]:text-xs " +
  "[&.ant-select-open_.ant-select-selector]:border-[#40a9ff] [&.ant-select-open_.ant-select-selector]:shadow-[0_0_0_2px_rgba(24,144,255,0.2)] " +
  "[&.ant-select-open_.ant-select-arrow]:rotate-180";

const dropdownPopupClass =
  "assessor-select-dropdown rounded border border-[#d9d9d9] shadow-[0_4px_12px_rgba(0,0,0,0.15)] " +
  "[&_.ant-select-item]:px-3 [&_.ant-select-item]:py-2 [&_.ant-select-item]:text-sm [&_.ant-select-item]:text-[#262626] " +
  "[&_.ant-select-item-option-active]:bg-[#f5f5f5] [&_.ant-select-item-option-selected]:bg-[#e6f7ff] [&_.ant-select-item-option-selected]:font-medium [&_.ant-select-item-option-selected]:text-[#1890ff] " +
  "[&_.ant-select-item-empty]:px-4 [&_.ant-select-item-empty]:py-4 [&_.ant-select-item-empty]:text-center [&_.ant-select-item-empty]:text-[#8c8c8c]";

export interface AssessorAssignBodyProps {
  visible: boolean;
  groupNo: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export type AssessorAssignBodyRef = {
  submit: () => Promise<void>;
  reset: () => void;
};

export const AssessorAssignBody = forwardRef<AssessorAssignBodyRef, AssessorAssignBodyProps>(
  ({ visible, groupNo, onCancel, onSuccess }, ref) => {
    const onCancelRef = useRef(onCancel);
    const onSuccessRef = useRef(onSuccess);
    onCancelRef.current = onCancel;
    onSuccessRef.current = onSuccess;

    const [members, setMembers] = useState<Profile.GroupMember[]>([]);
    const [selectedAssessorId, setSelectedAssessorId] = useState<number | undefined>(undefined);
    const [membersLoading, setMembersLoading] = useState(false);

    useEffect(() => {
      if (visible && groupNo) {
        setMembersLoading(true);
        getGroupMembersByGroupNoApi(groupNo, true)
          .then((membersList) => {
            setMembers(membersList);
          })
          .catch((error) => {
            console.error("获取团队成员失败:", error);
            message.error("获取团队成员失败，请重试");
          })
          .finally(() => {
            setMembersLoading(false);
          });
      }
    }, [visible, groupNo]);

    const reset = () => {
      setSelectedAssessorId(undefined);
      setMembers([]);
    };

    const submit = async () => {
      if (!selectedAssessorId) {
        message.error("请选择审核者");
        return;
      }
      try {
        await assignAssessorApi(groupNo, [selectedAssessorId]);
        message.success("审核者设置成功");
        reset();
        onCancelRef.current();
        onSuccessRef.current();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { info?: string } } };
        console.error("设置审核者失败:", error);
        message.error(err?.response?.data?.info || "设置审核者失败，请重试");
      }
    };

    useImperativeHandle(ref, () => ({ submit, reset }), [selectedAssessorId, groupNo]);

    const options = members.map((member) => ({
      value: member.user_id,
      label: member.username,
      disabled: false,
    }));

    return (
      <div className={bodyClass}>
        <div className="modal-text">请选择审核者</div>

        {membersLoading ? (
          <div className={loadingRowClass}>
            <Spin size="small" />
            <span className={loadingTextClass}>正在加载团队成员...</span>
          </div>
        ) : (
          <Select
            value={selectedAssessorId}
            onChange={setSelectedAssessorId}
            placeholder="请选择审核者"
            className={selectClass}
            popupClassName={dropdownPopupClass}
            options={options}
            showSearch
            allowClear={false}
            dropdownMatchSelectWidth={true}
            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
            filterOption={(input, option) =>
              (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={members.length === 0 ? "暂无团队成员" : "未找到匹配的成员"}
            dropdownStyle={{
              maxHeight: 200,
              overflow: "auto",
            }}
          />
        )}
      </div>
    );
  }
);

AssessorAssignBody.displayName = "AssessorAssignBody";
